/** Approximate legend entries per row when Plotly wraps a horizontal legend. */
const DEFAULT_ITEMS_PER_ROW = 5;

/** Marker + gap before label text in Plotly’s horizontal legend. */
const LEGEND_MARKER_PAD_PX = 36;

function horizontalLegendRowCount(traceCount, itemsPerRow) {
  const n = Math.max(1, Number(traceCount) || 1);
  const perRow = Math.max(1, itemsPerRow);
  return Math.ceil(n / perRow);
}

/**
 * Default minimum width (px) per horizontal legend entry so labels do not collide
 * with the next trace’s marker (e.g. long “Species - Tag … (#)” names).
 */
function defaultLegendEntryWidthPx(isMobile) {
  return isMobile ? 156 : 200;
}

function maxLegendEntryWidthPx(isMobile) {
  return isMobile ? 320 : 480;
}

/**
 * Estimate pixels needed for the longest legend label (icon + text).
 * @param {string[]} labels
 */
function legendEntryWidthFromLabels(labels, isMobile, { legendFontSize, minPx } = {}) {
  const minDefault =
    typeof minPx === "number" && Number.isFinite(minPx)
      ? minPx
      : defaultLegendEntryWidthPx(isMobile);
  if (!Array.isArray(labels) || labels.length === 0) {
    return minDefault;
  }
  const charPx =
    typeof legendFontSize === "number" && Number.isFinite(legendFontSize)
      ? legendFontSize * 0.55
      : isMobile
        ? 6.4
        : 7.2;
  const longest = labels.reduce(
    (max, label) => Math.max(max, String(label ?? "").length),
    0
  );
  const estimated = LEGEND_MARKER_PAD_PX + longest * charPx;
  return Math.min(
    maxLegendEntryWidthPx(isMobile),
    Math.max(minDefault, Math.ceil(estimated))
  );
}

/**
 * Plotly: horizontal legend above the cartesian plot (in the top margin).
 *
 * Plotly’s default `legend.yref` / `xref` of `"paper"` refers to the **plotting
 * area only**, so `y: 1` sits on the top edge of the data region and the legend
 * grows downward over the chart. We use `"container"` so `y: 1` is the top of
 * the full figure; with `margin.t` reserved, the legend stays outside the axes.
 *
 * @param {number} traceCount — number of traces shown in the legend
 * @param {boolean} isMobile
 * @param {object} [options]
 * @param {number} [options.itemsPerRow] — upper bound for wrap estimate (default 5)
 * @param {number} [options.legendEntryWidthPx] — Plotly `legend.entrywidth` (pixels); minimum floor before label sizing
 * @param {string[]} [options.legendLabels] — trace names; widens `entrywidth` to fit the longest label
 * @param {number} [options.legendFontSize] — when set, row height scales with label size
 * @param {object} [options] — any other keys are spread onto `layout.legend`
 * @returns {{ marginTop: number, extraHeight: number, legend: object }}
 */
export function plotlyHorizontalLegendTop(traceCount, isMobile, options = {}) {
  const {
    itemsPerRow = DEFAULT_ITEMS_PER_ROW,
    legendFontSize,
    legendEntryWidthPx: legendEntryWidthOpt,
    legendLabels,
    ...legendRest
  } = options;

  const legendEntryFloorPx =
    typeof legendEntryWidthOpt === "number" && Number.isFinite(legendEntryWidthOpt)
      ? legendEntryWidthOpt
      : defaultLegendEntryWidthPx(isMobile);

  const legendEntryWidthPx = legendEntryWidthFromLabels(
    Array.isArray(legendLabels) ? legendLabels : [],
    isMobile,
    { legendFontSize, minPx: legendEntryFloorPx }
  );

  // Align margin reservation with Plotly wrapping when each entry has a minimum width.
  const approxPlotInnerW = isMobile ? 340 : 920;
  const estimatedPerRow = Math.max(
    2,
    Math.min(itemsPerRow, Math.floor(approxPlotInnerW / legendEntryWidthPx))
  );
  const rows = horizontalLegendRowCount(traceCount, estimatedPerRow);
  const baseRowPx = isMobile ? 22 : 26;
  const rowPx =
    typeof legendFontSize === "number" && Number.isFinite(legendFontSize)
      ? Math.max(baseRowPx, Math.round(legendFontSize * 1.28))
      : baseRowPx;
  const padding = isMobile ? 18 : 26;
  const minMarginTop = Math.max(isMobile ? 64 : 76, padding + rowPx);
  const marginTop = Math.min(
    400,
    Math.max(isMobile ? 64 : 76, padding + rows * rowPx)
  );
  // Keep plot area stable as legend wraps to additional rows.
  const extraHeight = Math.max(0, marginTop - minMarginTop);

  /** Pixels: horizontal legends default to maxheight 0.5 of layout, which can clip; allow the reserved margin band. */
  const maxheightPx = Math.min(380, Math.max(96, marginTop + 24));

  return {
    marginTop,
    extraHeight,
    legend: {
      orientation: "h",
      x: 0.5,
      xanchor: "center",
      xref: "container",
      yref: "container",
      y: 1,
      yanchor: "top",
      maxheight: maxheightPx,
      entrywidth: legendEntryWidthPx,
      entrywidthmode: "pixels",
      ...legendRest,
    },
  };
}

/**
 * Horizontal legend layout sized from Plotly trace `name` strings.
 * @param {Array<{ name?: string }>} traces
 */
export function plotlyHorizontalLegendForTraces(traces, isMobile, options = {}) {
  const labels = (Array.isArray(traces) ? traces : [])
    .map((t) => t?.name)
    .filter((n) => n != null && String(n).length > 0);
  return plotlyHorizontalLegendTop(labels.length || 1, isMobile, {
    legendLabels: labels,
    ...options,
  });
}
